import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { DollarSign, Target, Calendar, TrendingUp, Search, User, Check } from 'lucide-react';
import type { Stage } from './types';
import { useWorkspace } from '@/context/WorkspaceContext';

interface NewDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: Stage[];
  defaultStageId?: string;
  onSubmit: (data: {
    title: string;
    description?: string;
    value?: number;
    priority: string;
    source?: string;
    expectedCloseDate?: string;
    stageId?: string;
    probability?: number;
    clientInfo?: Record<string, any>;
    contactId?: string;
  }) => Promise<void>;
}



export const NewDealDialog: React.FC<NewDealDialogProps> = ({
  open, onOpenChange, stages, defaultStageId, onSubmit,
}) => {
  // Deal State
  const [dealTitle, setDealTitle] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [priority, setPriority] = useState('medium');

  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [stageId, setStageId] = useState(defaultStageId || '');
  const [probability, setProbability] = useState('');
  
  
  const [linkedContactId, setLinkedContactId] = useState<string>('none');
  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [contactTab, setContactTab] = useState<'lead' | 'client'>('lead');
  const [contactSearch, setContactSearch] = useState('');
  const { currentWorkspace } = useWorkspace();
  
  React.useEffect(() => {
    if (open && currentWorkspace?.id) {
      import('@/lib/leadService').then(({ leadService }) => {
        leadService.getLeadsForDealPicker(currentWorkspace.id).then(contacts => {
          setAvailableContacts(contacts);
        });
      });
    }
  }, [open, currentWorkspace]);
  
  const handleContactSelect = (contactId: string) => {
    setLinkedContactId(contactId);
  };

  const [submitting, setSubmitting] = useState(false);

  const activeStages = stages.filter(s => !s.isWon && !s.isLost);

  const filteredContacts = availableContacts
    .filter(c => c.type === contactTab)
    .filter(c => {
      if (!contactSearch.trim()) return true;
      const term = contactSearch.toLowerCase();
      const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      return name.includes(term) || (c.email || '').toLowerCase().includes(term);
    })
    .slice(0, 3);

  React.useEffect(() => {
    if (open) {
      if (defaultStageId) {
        setStageId(defaultStageId);
      } else if (activeStages.length > 0) {
        setStageId(activeStages[0].id);
      }
    }
  }, [open, defaultStageId]); // Initialize stageId on open

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dealTitle.trim() || linkedContactId === 'none') return;

    try {
      setSubmitting(true);
      await onSubmit({
        title: dealTitle.trim(),
        description: description.trim() || undefined,
        value: value ? parseFloat(value) : undefined,
        priority,

        expectedCloseDate: expectedCloseDate || undefined,
        stageId: stageId || undefined,
        probability: probability ? parseInt(probability) : undefined,
        contactId: linkedContactId !== 'none' ? linkedContactId : undefined,
      });
      // Reset all fields
      setDealTitle(''); setDescription(''); setValue(''); setPriority('medium');
      const active = stages.filter(s => !s.isWon && !s.isLost);
      setExpectedCloseDate(''); setStageId(defaultStageId || (active.length > 0 ? active[0].id : '')); setProbability('');
      setLinkedContactId('none');
      
      onOpenChange(false);
    } catch (err) {
      console.error('Error creating deal:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-card border-border p-0 overflow-hidden gap-0">
        <DialogDescription className="sr-only">Create a new deal and add deal info</DialogDescription>
        <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
          <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-workspace-accent/15 flex items-center justify-center">
              <Target className="w-4 h-4 text-workspace-accent" />
            </div>
            Create New Deal
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col max-h-[80vh]">
          <div className="flex-1 overflow-y-auto flex flex-col md:flex-row md:divide-x md:divide-border">
            
            {/* LEFT COLUMN: Deal Title & Status */}
            <div className="flex-1 flex flex-col p-6 space-y-5">
            
              {/* Deal Title */}
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm font-medium">Deal Title <span className="text-red-500">*</span></Label>
                <Input autoFocus placeholder="Enter Deal Title" value={dealTitle} onChange={e => setDealTitle(e.target.value)} className="bg-background border-input h-10" required />
              </div>

            {/* Status Section Fields */}
            <div className="space-y-3">
              
              {/* Win Probability - Full Width */}
              <div className="space-y-1.5 flex flex-col justify-center">
                <Label className="text-foreground text-sm font-medium flex items-center gap-1 mb-2 h-5">
                  <TrendingUp className="w-3.5 h-3.5" /> Win Probability %
                </Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[probability ? parseInt(probability) : 0]}
                    onValueChange={(v) => setProbability(v[0].toString())}
                    max={100}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-8 text-right text-muted-foreground">{probability || 0}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm font-medium flex items-center gap-1 h-5">
                    <DollarSign className="w-3.5 h-3.5" /> Value
                  </Label>
                  <Input type="number" min="0" step="0.01" placeholder="0.00" value={value} onChange={e => setValue(e.target.value)} className="bg-background border-input h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm font-medium flex items-center h-5">Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="bg-background border-input h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm font-medium flex items-center h-5">Stage</Label>
                  <Select value={stageId} onValueChange={setStageId}>
                    <SelectTrigger className="bg-background border-input h-10">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeStages.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm font-medium flex items-center gap-1 h-5"><Calendar className="w-3.5 h-3.5" /> Expected Close</Label>
                  <Input type="date" value={expectedCloseDate} onChange={e => setExpectedCloseDate(e.target.value)} className="bg-background border-input h-10" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5 mt-2">
              <Label className="text-foreground text-sm font-medium">Notes</Label>
              <Textarea 
                placeholder="Brief notes for this deal..." 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 resize-none text-sm text-foreground focus-visible:ring-yellow-400 min-h-[6rem]" 
                rows={4} 
              />
            </div>
            
            </div>

            {/* RIGHT COLUMN: Contact */}
            <div className="flex-1 flex flex-col p-6 space-y-6">
              
              {/* Link Contact */}
              <div className="space-y-3">
                <Label className="text-foreground text-sm font-medium">Link Contact <span className="text-red-500">*</span></Label>
                
                <div className="border border-border rounded-lg overflow-hidden bg-background">
                  <Tabs value={contactTab} onValueChange={(v) => { setContactTab(v as 'lead' | 'client'); setContactSearch(''); }} className="w-full">
                    <TabsList className="w-full rounded-none border-b border-border bg-muted/30 h-10 p-0">
                      <TabsTrigger value="lead" className="flex-1 rounded-none data-[state=active]:bg-background data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent">Leads</TabsTrigger>
                      <TabsTrigger value="client" className="flex-1 rounded-none data-[state=active]:bg-background data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent">Clients</TabsTrigger>
                    </TabsList>
                    
                    <div className="p-3">
                      <div className="relative mb-3">
                        <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder={`Search ${contactTab === 'lead' ? 'leads' : 'clients'}...`}
                          value={contactSearch}
                          onChange={e => setContactSearch(e.target.value)}
                          className="pl-8 h-8 text-sm bg-background border-input"
                        />
                      </div>
                      
                      <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                        {filteredContacts.length > 0 ? (
                          filteredContacts.map(c => (
                            <div
                              key={c.id}
                              onClick={() => handleContactSelect(c.id)}
                              className={`flex items-center justify-between p-2 rounded-md cursor-pointer text-sm transition-colors ${
                                linkedContactId === c.id 
                                  ? 'bg-workspace-accent/10 border border-workspace-accent/20' 
                                  : 'hover:bg-muted/50 border border-transparent'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                                </div>
                                <div className="flex flex-col overflow-hidden">
                                  <span className="font-medium truncate">{c.first_name} {c.last_name}</span>
                                  {c.email && <span className="text-xs text-muted-foreground truncate">{c.email}</span>}
                                </div>
                              </div>
                              {linkedContactId === c.id && (
                                <Check className="w-4 h-4 text-workspace-accent shrink-0" />
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-sm text-muted-foreground">
                            No {contactTab}s found
                          </div>
                        )}
                      </div>
                    </div>
                  </Tabs>
                </div>
              </div>

            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/10 shrink-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!dealTitle.trim() || linkedContactId === 'none' || submitting}
              className="bg-workspace-accent text-white hover:bg-workspace-accent-hover min-w-[140px]"
            >
              {submitting ? 'Creating...' : '+ Add Deal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
